import React, { useRef } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Printer, X } from 'lucide-react';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

const formatConsentDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return format(date, 'd MMMM yyyy', { locale: ro });
  } catch {
    return dateStr;
  }
};

const CONSENT_TEXT = {
  title: 'Formular de consim\u{021B}\u{0103}m\u{00E2}nt privind prelucrarea datelor cu caracter personal',
  intro: 'Prin prezentul document, \u{00EE}mi exprim acordul informat, \u{00EE}n mod liber \u{0219}i neechivoc, ca datele mele cu caracter personal s\u{0103} fie colectate \u{0219}i prelucrate \u{00EE}n baza de date a cabinetului prin intermediul aplica\u{021B}iei de management medical.',
  s1_title: '1. Categorii de date prelucrate',
  s1_body: 'Am luat la cuno\u{0219}tin\u{021B}\u{0103} c\u{0103} aplica\u{021B}ia va stoca urm\u{0103}toarele date:',
  s1_id: 'date de identificare:',
  s1_id_body: ' Nume, prenume, CNP, data na\u{0219}terii, sex.;',
  s1_contact: ' date de contact:',
  s1_contact_body: ' num\u{0103}r de telefon, adres\u{0103} de e-mail, adresa de domiciliu, contact de urgen\u{021B}\u{0103};',
  s1_med: ' date biometrice \u{0219}i medicale:',
  s1_med_body: ' \u{00EE}n\u{0103}l\u{021B}ime, greutate, grup\u{0103} sanguin\u{0103}, alergii, diagnostice (coduri ICD), parametri de laborator (Glicemie, HbA1c, TA, profil lipidic, etc), simptome, examen clinic, medica\u{021B}ie prescris\u{0103} \u{0219}i recomand\u{0103}ri de stil de via\u{021B}\u{0103}.',
  s2_title: '2. Scopul prelucr\u{0103}rii',
  s2_body: 'Datele sunt colectate exclusiv pentru: asigurarea actului medical (diagnosticare, monitorizarea diabetului, prescriere tratament); gestionarea program\u{0103}rilor \u{0219}i comunicarea cu pacientul; men\u{021B}inerea istoricului medical conform obliga\u{021B}iilor legale de arhivare; interven\u{021B}ii rapide \u{00EE}n caz de urgen\u{021B}\u{0103} (prin contactarea persoanei desemnate).',
  s3_title: '3. Temeiul juridic',
  s3_body: 'Prelucrarea este necesar\u{0103} pentru furnizarea de asisten\u{021B}\u{0103} medical\u{0103} (Art. 9 alin. (2) lit. (h) din GDPR) \u{0219}i pentru \u{00EE}ndeplinirea obliga\u{021B}iilor legale ale operatorului.',
  s4_title: '4. Durata stoc\u{0103}rii',
  s4_body: 'Datele vor fi p\u{0103}strate pe durata necesar\u{0103} furniz\u{0103}rii serviciilor medicale, plus perioada prev\u{0103}zut\u{0103} de legisla\u{021B}ia na\u{021B}ional\u{0103} privind arhivarea dosarelor medicale.',
  s5_title: '5. Drepturile pacientului',
  s5_intro: 'Am fost informat c\u{0103}, \u{00EE}n conformitate cu Regulamentul (UE) 2016/679 (GDPR), beneficiez de urm\u{0103}toarele drepturi:',
  s5_acces: ' dreptul de acces:',
  s5_acces_b: ' s\u{0103} solicit o copie a datelor mele;',
  s5_rect: ' dreptul la rectificare:',
  s5_rect_b: ' s\u{0103} cer corectarea datelor eronate;',
  s5_sterg: ' dreptul la \u{0219}tergere',
  s5_sterg_b: ' (\u{201E}dreptul de a fi uitat\u{201D}): \u{00EE}n limitele legale care nu contravin obliga\u{021B}iei de p\u{0103}strare a dosarului medical;',
  s5_restr: ' dreptul la restric\u{021B}ionarea prelucr\u{0103}rii',
  s5_restr_b: ' sau portabilitatea datelor;',
  s5_retrag: ' dreptul de a retrage consim\u{021B}\u{0103}m\u{00E2}ntul:',
  s5_retrag_b: ' retragerea nu afecteaz\u{0103} legalitatea prelucr\u{0103}rii efectuate \u{00EE}nainte de aceasta.',
  decl_title: 'Declara\u{021B}ie de consim\u{021B}\u{0103}m\u{00E2}nt',
  check1: 'DA, sunt de acord cu prelucrarea datelor mele cu caracter personal, inclusiv a datelor medicale sensibile, \u{00EE}n scopul monitoriz\u{0103}rii \u{0219}i tratamentului \u{00EE}n cadrul acestui cabinet.',
  check2: 'DA, sunt de acord s\u{0103} fiu contactat (prin telefon/e-mail) pentru comunic\u{0103}ri legate de starea mea de s\u{0103}n\u{0103}tate sau program\u{0103}ri.',
  lbl_name: 'Nume \u{0219}i Prenume Pacient:',
  lbl_date: 'Data:',
  lbl_sign: 'Semn\u{0103}tura:',
  sign_missing: 'Semn\u{0103}tura nu a fost capturat\u{0103}',
};

const T = CONSENT_TEXT;

export const ConsentModal = ({ open, onOpenChange, patient }) => {
  const contentRef = useRef(null);

  if (!patient) return null;

  const fullName = `${patient.last_name} ${patient.first_name}`;
  const consentDate = formatConsentDate(patient.consent_date);
  const signature = patient.signature;

  const handlePrint = () => {
    const printContent = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Consim\u{021B}\u{0103}m\u{00E2}nt GDPR - ${fullName}</title>
          <style>
            @page { margin: 12mm 16mm 15mm 16mm; size: A4 portrait; }
            body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; line-height: 1.35; color: #000; margin: 0; }
            h1 { font-size: 12pt; text-align: center; font-weight: bold; margin: 0 0 10pt 0; text-transform: uppercase; line-height: 1.3; }
            .operator { margin-bottom: 6pt; }
            .operator p { margin: 0; }
            p { margin: 0 0 5pt 0; }
            .section { margin-top: 5pt; }
            .section-title { font-weight: bold; margin: 0 0 2pt 0; }
            .checkbox { margin: 6pt 0; }
            .checkbox-item { margin-bottom: 4pt; display: flex; align-items: flex-start; gap: 5pt; }
            .box { display: inline-block; width: 11pt; height: 11pt; border: 1.5pt solid #000; text-align: center; line-height: 11pt; font-size: 9pt; flex-shrink: 0; margin-top: 1pt; }
            .footer-fields { margin-top: 8pt; border-top: 0.5pt solid #aaa; padding-top: 6pt; }
            .footer-fields p { margin: 2pt 0; }
            .signature-img { max-width: 280px; max-height: 120px; margin-top: 2pt; }
            .bold { font-weight: bold; }
            .page-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 8pt; color: #888; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <h1>${T.title.toUpperCase()}</h1>
          <div class="operator">
            <p><span class="bold">Operator de date:</span> Dr. Maria Antonescu-Paraipan</p>
            <p><span class="bold">Reprezentat prin:</span> Maria Antonescu-Paraipan</p>
          </div>
          <p>${T.intro}</p>
          <div class="section">
            <p class="section-title">${T.s1_title}</p>
            <p>${T.s1_body}<span class="bold">${T.s1_id}</span>${T.s1_id_body}<span class="bold">${T.s1_contact}</span>${T.s1_contact_body}<span class="bold">${T.s1_med}</span>${T.s1_med_body}</p>
          </div>
          <div class="section">
            <p class="section-title">${T.s2_title}</p>
            <p>${T.s2_body}</p>
          </div>
          <div class="section">
            <p class="section-title">${T.s3_title}</p>
            <p>${T.s3_body}</p>
          </div>
          <div class="section">
            <p class="section-title">${T.s4_title}</p>
            <p>${T.s4_body}</p>
          </div>
          <div class="section">
            <p class="section-title">${T.s5_title}</p>
            <p>${T.s5_intro}<span class="bold">${T.s5_acces}</span>${T.s5_acces_b}<span class="bold">${T.s5_rect}</span>${T.s5_rect_b}<span class="bold">${T.s5_sterg}</span>${T.s5_sterg_b}<span class="bold">${T.s5_restr}</span>${T.s5_restr_b}<span class="bold">${T.s5_retrag}</span>${T.s5_retrag_b}</p>
          </div>
          <p class="section-title" style="margin-top: 6pt;">${T.decl_title}</p>
          <div class="checkbox">
            <div class="checkbox-item"><span class="box">X</span><span>${T.check1}</span></div>
            <div class="checkbox-item"><span class="box">X</span><span>${T.check2}</span></div>
          </div>
          <div class="footer-fields">
            <p><span class="bold">${T.lbl_name}</span> ${fullName}</p>
            <p><span class="bold">${T.lbl_date}</span> ${consentDate}</p>
            <p><span class="bold">${T.lbl_sign}</span></p>
            ${signature ? `<img src="${signature}" class="signature-img" />` : '<p style="color: #999;">____________________________</p>'}
          </div>
          <div class="page-footer">Page 1 of 1</div>
        </body>
      </html>
    `;
    const w = window.open('', '_blank');
    w.document.write(printContent);
    w.document.close();
    w.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-3 bg-white border-b">
          <h2 className="font-bold text-lg text-slate-900">Consim{'ț'}{'ă'}m{'â'}nt GDPR</h2>
          <div className="flex items-center gap-2">
            <Button onClick={handlePrint} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="consent-print-btn">
              <Printer className="w-4 h-4 mr-2" /> Printează
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} data-testid="consent-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div ref={contentRef} className="px-8 py-6 text-sm leading-relaxed text-slate-800" style={{ fontFamily: "'Times New Roman', Times, serif" }}>
          <h1 className="text-base font-bold text-center uppercase mb-4 tracking-wide leading-snug">
            {T.title}
          </h1>

          <div className="mb-3">
            <p><strong>Operator de date:</strong> Dr. Maria Antonescu-Paraipan</p>
            <p><strong>Reprezentat prin:</strong> Maria Antonescu-Paraipan</p>
          </div>

          <p className="mb-3">{T.intro}</p>

          <div className="mb-2">
            <p className="font-bold">{T.s1_title}</p>
            <p>{T.s1_body}<strong>{T.s1_id}</strong>{T.s1_id_body}<strong>{T.s1_contact}</strong>{T.s1_contact_body}<strong>{T.s1_med}</strong>{T.s1_med_body}</p>
          </div>

          <div className="mb-2">
            <p className="font-bold">{T.s2_title}</p>
            <p>{T.s2_body}</p>
          </div>

          <div className="mb-2">
            <p className="font-bold">{T.s3_title}</p>
            <p>{T.s3_body}</p>
          </div>

          <div className="mb-2">
            <p className="font-bold">{T.s4_title}</p>
            <p>{T.s4_body}</p>
          </div>

          <div className="mb-2">
            <p className="font-bold">{T.s5_title}</p>
            <p>{T.s5_intro}<strong>{T.s5_acces}</strong>{T.s5_acces_b}<strong>{T.s5_rect}</strong>{T.s5_rect_b}<strong>{T.s5_sterg}</strong>{T.s5_sterg_b}<strong>{T.s5_restr}</strong>{T.s5_restr_b}<strong>{T.s5_retrag}</strong>{T.s5_retrag_b}</p>
          </div>

          <p className="font-bold mt-3 mb-2">{T.decl_title}</p>

          <div className="space-y-2 mb-4">
            <div className="flex items-start gap-2">
              <span className="inline-flex items-center justify-center w-4 h-4 border-2 border-slate-800 rounded-sm text-[10px] font-bold flex-shrink-0 mt-0.5">X</span>
              <span>{T.check1}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="inline-flex items-center justify-center w-4 h-4 border-2 border-slate-800 rounded-sm text-[10px] font-bold flex-shrink-0 mt-0.5">X</span>
              <span>{T.check2}</span>
            </div>
          </div>

          <div className="border-t-2 border-slate-300 pt-3 mt-4 space-y-1">
            <p><strong>{T.lbl_name}</strong> {fullName}</p>
            <p><strong>{T.lbl_date}</strong> {consentDate}</p>
            <div>
              <p className="font-bold">{T.lbl_sign}</p>
              {signature ? (
                <img src={signature} alt="Semnatura pacient" className="max-w-[280px] max-h-[120px] mt-2" data-testid="consent-signature-img" />
              ) : (
                <p className="text-slate-400 italic mt-1">{T.sign_missing}</p>
              )}
            </div>
          </div>

          <div className="text-center text-xs text-slate-400 mt-4 pt-2 border-t border-slate-200">
            Page 1 of 1
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
